import {
  Button,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router';
import { fontWeights } from '@/@mui/tokens/font-weights';
import { BaseDialog } from '@/components/shared/base-dialog';
import { StateMessage } from '@/components/shared/state-message';
import { useWizard } from '@/context/wizard';
import { useAvailableSubscriptions } from '@/data/subsidy/available-subscriptions';
import type { Course } from '@/models/catalog/course';
import type { RefundRequestWizardProps } from '@/packages/subsidy/pages/refund-request/steps/resource-description/interfaces';
import { formatDate } from '@/utils/format-date';
import { formatMoney } from '@/utils/format-money';
import { getSvg } from '@/utils/get-svg';

const Option = ({ subscription }: { subscription: Course }) => {
  const { institution, course_name, original_price, subscribed_at } =
    subscription;

  return (
    <Stack spacing={1} width="100%" alignItems="start">
      <Stack direction="row" justifyContent="space-between" width="100%">
        <Typography fontWeight={fontWeights.fontWeightSemibold}>
          {institution?.name}
        </Typography>
        <Typography>Inscrição: {formatDate(subscribed_at)}</Typography>
      </Stack>
      <Typography variant="body2">{course_name}</Typography>
      <Typography variant="body2">
        {formatMoney(original_price ?? '')}
      </Typography>
    </Stack>
  );
};

interface EnrollmentSelectionDialogProps {
  onClose: () => void;
  onSelect: (value: Course | null) => void;
  value: Course | null;
  onChange: (value: Course | null) => void;
}

export const EnrollmentSelectionDialog = ({
  onClose,
  onSelect,
  value,
  onChange,
}: EnrollmentSelectionDialogProps) => {
  const navigate = useNavigate();
  const { values } = useWizard<RefundRequestWizardProps>();
  const { subscriptions, error, isFetching } = useAvailableSubscriptions(
    values?.selectedPolicy
  );

  const hasContent = subscriptions && subscriptions?.length > 0 && !error;
  const notFound = !isFetching && !hasContent;

  const handleSelect = (id: number) => {
    const selected = subscriptions?.find((s) => s.id === id);
    onChange(selected?.id === value?.id ? null : (selected ?? null));
  };

  const handleButtonClick = () => {
    if (notFound) return navigate('/cursos');

    onSelect(value);
    onClose();
  };

  return (
    <BaseDialog
      open
      onClose={onClose}
      actionsComponent={
        <>
          <Button variant="text" onClick={onClose}>
            Fechar
          </Button>
          <Button
            variant="contained"
            disabled={!value && !notFound}
            onClick={handleButtonClick}
          >
            {notFound ? 'Ir para o catálogo' : 'Selecionar'}
          </Button>
        </>
      }
    >
      {notFound ? (
        <StateMessage
          backgroundColor="transparent"
          image={getSvg('empty_state_catalog.svg')}
          imageSize={{ width: 130 }}
          title="Você ainda não tem inscrição na Galena."
          description="Infelizmente não encontramos nenhuma inscrição. Você pode fazê-la encontrando um curso no nosso catálogo."
          centered={false}
        />
      ) : (
        <>
          <Typography variant="h2">
            Parece que seu curso não está mais no catálogo
          </Typography>
          <Typography my={2}>
            Não se preocupe! Temos o registro das suas inscrições. Selecione
            abaixo a inscrição do curso que você quer pedir reembolso.
          </Typography>
          <Stack spacing={2}>
            {isFetching && (
              <Stack spacing={2}>
                {[...Array(3)].map((_, index) => (
                  <Skeleton
                    key={index}
                    variant="rounded"
                    height={100}
                    data-testid="skeleton"
                  />
                ))}
              </Stack>
            )}
            {!isFetching && hasContent && (
              <Stack maxHeight={400} spacing={2} overflow="auto">
                <ToggleButtonGroup
                  value={value?.id || null}
                  exclusive
                  onChange={(_, id) => handleSelect(id)}
                  orientation="vertical"
                  fullWidth
                  size="large"
                >
                  <Stack spacing={2}>
                    {subscriptions?.map((subscription) => (
                      <ToggleButton
                        key={subscription.id}
                        value={subscription.id}
                        sx={{ textTransform: 'none', textAlign: 'left' }}
                      >
                        <Option subscription={subscription} />
                      </ToggleButton>
                    ))}
                  </Stack>
                </ToggleButtonGroup>
              </Stack>
            )}
          </Stack>
        </>
      )}
    </BaseDialog>
  );
};
